
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { PropBot } from '@/types/prop-bots';
import { Shield, ShieldCheck, ShieldAlert, Lock } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useIsMobile } from '@/hooks/use-mobile';

interface EnhancedPropBotRulesProps {
  bot: PropBot;
  challengeRules?: string[];
  isLoading?: boolean;
}

export const EnhancedPropBotRules = ({ 
  bot, 
  challengeRules: propRules, 
  isLoading 
}: EnhancedPropBotRulesProps) => {
  const { t } = useTranslation();
  
  // Robust extraction of propDetails - handle both array and object formats
  const propDetails = Array.isArray(bot.propDetails) && bot.propDetails.length > 0 
    ? bot.propDetails[0] 
    : bot.propDetails;
    
  const rules = propRules || propDetails?.challenge_rules || [];
  const isMobile = useIsMobile();

  // Icon rotation for rules
  const ruleIcons = [Shield, ShieldCheck, ShieldAlert, Lock];

  return (
    <Card data-tour="prop-bot-rules" className="relative overflow-hidden">
      {/* Background pattern */}
      <div className="absolute inset-0 bg-gradient-to-br from-yellow-500/5 via-background to-orange-500/5" />
      <div className="absolute bottom-0 right-0 w-32 h-32 bg-gradient-to-tl from-yellow-500/10 to-transparent rounded-full blur-2xl" />
      
      <CardHeader className={`${isMobile ? "p-4" : "p-6"} pb-4 relative`}>
        <CardTitle className={`${isMobile ? "text-lg" : "text-xl"} flex items-center gap-2`}>
          <div className="w-1 h-6 bg-gradient-to-b from-yellow-500 to-orange-500 rounded-full" />
          {t('challenge_rules')}
        </CardTitle>
      </CardHeader>
      
      <CardContent className={`${isMobile ? "px-4 pb-4" : "px-6 pb-6"} relative`}>
        {rules.length === 0 ? (
          <div className="text-center py-8">
            <div className="w-16 h-16 mx-auto mb-4 bg-muted/50 rounded-full flex items-center justify-center">
              <Shield className="h-8 w-8 text-muted-foreground" />
            </div>
            <p className="text-muted-foreground">{t('no_rules_available')}</p>
          </div>
        ) : (
          <div className="grid gap-3">
            {rules.map((rule, index) => {
              const IconComponent = ruleIcons[index % ruleIcons.length];
              
              return (
                <div 
                  key={index} 
                  className="group flex items-start gap-3 p-3 rounded-xl border border-border/50 bg-gradient-to-r from-background via-muted/20 to-background transition-all duration-300 hover:shadow-md hover:border-border hover:-translate-y-0.5 animate-fade-in"
                  style={{ animationDelay: `${index * 100}ms` }}
                >
                  {/* Animated icon */}
                  <div className="flex-shrink-0 mt-0.5">
                    <div className="relative">
                      <div className="absolute inset-0 bg-gradient-to-br from-yellow-500/20 to-orange-500/20 rounded-full blur-sm opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                      <div className="relative w-8 h-8 bg-gradient-to-br from-yellow-500/20 to-yellow-500/10 rounded-full flex items-center justify-center border border-yellow-500/20 transition-all duration-300 group-hover:scale-110 group-hover:border-yellow-500/40">
                        <IconComponent className={`${isMobile ? "h-4 w-4" : "h-4 w-4"} text-yellow-500 transition-transform duration-300 group-hover:rotate-3`} />
                      </div>
                    </div>
                  </div>
                  
                  {/* Rule text */}
                  <div className="flex-1 min-w-0">
                    <span className={`${isMobile ? "text-sm" : "text-base"} font-medium leading-relaxed transition-colors duration-300 group-hover:text-foreground`}>
                      {rule}
                    </span>
                  </div>
                  
                  {/* Hover indicator */}
                  <div className="flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                    <div className="w-2 h-2 bg-gradient-to-br from-yellow-500 to-orange-500 rounded-full" />
                  </div>
                </div>
              );
            })}
          </div>
        )}
        
        {/* Rules count indicator */}
        {rules.length > 0 && (
          <div className="mt-4 pt-4 border-t border-border/30">
            <div className="flex items-center justify-center gap-2 text-sm text-muted-foreground">
              <ShieldCheck className="h-4 w-4 text-yellow-500" />
              <span>{rules.length} {t('rules_to_follow')}</span>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};
